import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useLanguage } from '../../contexts/LanguageContext';
import { firestore } from '../../lib/firebase/config';
import toast from 'react-hot-toast';

const AGREEMENT_URL = '/contracts/guides-arabic-agreement.pdf';

export const GuideAgreementGate = () => {
  const { translations } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [isSigned, setIsSigned] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [hasMissingGuide, setHasMissingGuide] = useState(false);

  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    let isActive = true;

    const fetchAgreementStatus = async () => {
      if (userRole !== 'guide' || !userId) {
        if (isActive) {
          setIsLoading(false);
          setIsSigned(true);
        }
        return;
      }

      setIsLoading(true);
      try {
        const guideRef = doc(firestore, 'Guides', userId);
        const guideSnap = await getDoc(guideRef);
        if (!guideSnap.exists()) {
          if (isActive) {
            setHasMissingGuide(true);
            setIsSigned(false);
          }
          return;
        }

        const guideData = guideSnap.data();
        if (isActive) {
          setIsSigned(Boolean(guideData.agreementSigned));
        }
      } catch (error) {
        console.error('Error loading guide agreement status:', error);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchAgreementStatus();

    return () => {
      isActive = false;
    };
  }, [userRole, userId]);

  useEffect(() => {
    if (userRole === 'guide' && !isSigned) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [userRole, isSigned]);

  const handleSign = async () => {
    if (!userId) return;

    setIsSubmitting(true);
    try {
      const guideRef = doc(firestore, 'Guides', userId);
      await updateDoc(guideRef, {
        agreementSigned: true,
        agreementSignedAt: Date.now(),
      });
      setIsSigned(true);
      toast.success(
        translations?.guideAgreement?.success || 'Agreement signed successfully'
      );
    } catch (error) {
      console.error('Error signing guide agreement:', error);
      toast.error(
        translations?.guideAgreement?.error ||
          'Failed to sign the agreement. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (userRole !== 'guide' || isSigned) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 z-30 bg-white">
      <div className="h-full overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.guideAgreement?.title || 'Guide Agreement'}
            </h1>
            <p className="text-gray-600">
              {translations?.guideAgreement?.subtitle ||
                'Please read and sign the agreement before continuing.'}
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              {translations?.guideAgreement?.loading || 'Loading agreement...'}
            </div>
          ) : hasMissingGuide ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
              {translations?.guideAgreement?.missing ||
                'Agreement data not found. Please contact support.'}
            </div>
          ) : (
            <>
              <div className="rounded-lg border bg-gray-50 p-3">
                <iframe
                  src={AGREEMENT_URL}
                  title="Guide Agreement"
                  className="w-full h-[65vh] rounded-md bg-white"
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(event) => setIsChecked(event.target.checked)}
                  className="mt-1 rounded text-primary-500 focus:ring-primary-500"
                />
                <label className="text-sm text-gray-700">
                  {translations?.guideAgreement?.checkbox ||
                    'I have read the agreement and agree to its terms.'}
                </label>
              </div>

              <button
                type="button"
                onClick={handleSign}
                disabled={!isChecked || isSubmitting}
                className={`w-full sm:w-auto px-6 py-2 rounded-lg text-white transition-colors ${
                  !isChecked || isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-500 hover:bg-primary-600'
                }`}
              >
                {isSubmitting
                  ? translations?.guideAgreement?.signing || 'Signing...'
                  : translations?.guideAgreement?.button || 'Sign and Continue'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
